import { RefObject, useEffect, useRef, useState } from "react";

// Radio is push-to-talk: long silence, then bursts when someone keys up. This
// taps the playing media element via Web Audio to expose a live RMS level and a
// held "active" flag, so the UI can signal a transmission (turn your volume up)
// rather than looking dead between bursts.
const ACTIVITY_THRESHOLD = 0.05;
const ACTIVITY_HOLD_MS = 1000;
const UPDATE_INTERVAL_MS = 50;

export const useAudioActivity = (
  // useHlsVideo types its element ref as video; here it drives an <audio>, and
  // Web Audio only needs the shared HTMLMediaElement surface.
  mediaRef: RefObject<HTMLVideoElement | null>,
  enabled: boolean,
  // The meter has `barCount` bars; RMS is scaled by `gain` (decoded voice is
  // quiet) and quantized to a lit-bar count here so published state only
  // changes when a bar actually flips — the raw float would re-render the meter
  // subtree ~15-20x/sec for the whole listening session.
  { barCount, gain }: { barCount: number; gain: number }
): { litBars: number; active: boolean } => {
  const [litBars, setLitBars] = useState(0);
  const [active, setActive] = useState(false);
  // A media element can back only one source node for its lifetime, so the
  // Web Audio graph is built once and reused across listen/stop toggles.
  const graphRef = useRef<{
    context: AudioContext;
    analyser: AnalyserNode;
    samples: Uint8Array<ArrayBuffer>;
  } | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastActiveAtRef = useRef(0);
  const lastUpdateAtRef = useRef(0);

  // The graph is reused across listen/stop toggles, so tear it down only when
  // the panel unmounts — otherwise navigating away leaks the AudioContext (and
  // its audio-thread processing graph) for the tab's lifetime.
  useEffect(
    () => () => {
      if (graphRef.current) {
        void graphRef.current.context.close();
        graphRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const element = mediaRef.current;
    if (!enabled || !element) return;

    if (!graphRef.current) {
      const context = new AudioContext();
      const source = context.createMediaElementSource(element);
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      // Fan out: destination keeps the audio audible; analyser only taps it.
      source.connect(context.destination);
      source.connect(analyser);
      graphRef.current = {
        context,
        analyser,
        samples: new Uint8Array(analyser.fftSize),
      };
    }

    const { context, analyser, samples } = graphRef.current;
    void context.resume();

    const tick = () => {
      const now = performance.now();
      // Sample only at the publish cadence: at 60fps the RMS loop would run
      // 3-4x more often than its result is ever read, and the hold window
      // (1s) is far coarser than the gap between samples.
      if (now - lastUpdateAtRef.current >= UPDATE_INTERVAL_MS) {
        lastUpdateAtRef.current = now;
        analyser.getByteTimeDomainData(samples);
        let sumSquares = 0;
        for (const sample of samples) {
          const centred = (sample - 128) / 128;
          sumSquares += centred * centred;
        }
        const rms = Math.sqrt(sumSquares / samples.length);
        if (rms > ACTIVITY_THRESHOLD) lastActiveAtRef.current = now;
        // Setting state to its current value is a no-op in React, so the meter
        // re-renders only when the lit-bar count or on-air flag changes.
        setLitBars(Math.min(barCount, Math.round(rms * barCount * gain)));
        setActive(now - lastActiveAtRef.current < ACTIVITY_HOLD_MS);
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
      setLitBars(0);
      setActive(false);
    };
  }, [enabled, mediaRef, barCount, gain]);

  return { litBars, active };
};
